/**
 * The mesh's Settings tabs that read and write more than the policy draft: Policy file
 * management (versions, who and when, restore), Domains (the mesh name suffix) and Keys (join
 * keys, from the Nodes view's store). Device management lives in the policy editor, which holds
 * the draft those settings edit.
 */

import type { MeshNode, MeshPolicyVersionView } from '@aioe/shared';
import { dateShort, dateTime } from '@aioe/shared/wording/dateTime';
import { Button } from '@heroui/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ConfirmDialog, ErrorNote, ListBlock, ListGroup, StatusMark, type ArtTone } from './ui.tsx';

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const when = (iso: string) => dateTime(iso, { date: 'medium' });

/** What a version changed, as the policy says it: grants, tests and the headline settings. */
function summaryOf(v: MeshPolicyVersionView): string {
  const p = v.policy as { grants?: unknown[]; tests?: unknown[]; keyLifetimeDays?: number; transport?: { mode?: string } } | undefined;
  return `${plural(p?.grants?.length ?? 0, 'grant')} · ${plural(p?.tests?.length ?? 0, 'test')} · keys last ${p?.keyLifetimeDays ? `${p.keyLifetimeDays} days` : 'for ever'}`;
}

export function PolicyFiles({ tone: _tone, onRestored }: { tone: ArtTone; onRestored: () => void }) {
  const versions = useLoad(() => routes.meshPolicyVersions(), []);
  const [restoring, setRestoring] = useState<MeshPolicyVersionView>();
  const [error, setError] = useState<string>();
  const list = versions.data?.versions ?? [];
  return (
    <div className="grid gap-4">
      <ListGroup title="Policy file management" count={versions.data ? list.length : undefined} description="Every published policy, newest first. Restoring one publishes it again, after the same tests."><ListBlock flush>
        <ErrorNote error={versions.error ?? error} />
        <ul className="divide-y divide-separator text-sm" role="list">
          {list.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0">
                <span className="block truncate">
                  <span className="font-medium">{v.savedByName ?? 'Someone'}</span> {v.restoredFrom ? 'restored an earlier policy' : 'published'}
                  {v.current && (
                    <span className="ml-2 align-middle">
                      <StatusMark state="online" label="In force" />
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-muted" title={when(v.savedAt)}>
                  {timeAgo(v.savedAt)} · {summaryOf(v)}
                </span>
              </span>
              {!v.current && (
                <Button size="sm" variant="secondary" onPress={() => setRestoring(v)}>
                  Restore
                </Button>
              )}
            </li>
          ))}
          {list.length === 0 && !versions.loading && <li className="px-4 py-3 text-muted">No policy has been published here yet. The next one is kept as the first version.</li>}
        </ul>
      </ListBlock></ListGroup>
      <ConfirmDialog
        isOpen={!!restoring}
        title="Restore this policy?"
        confirmLabel="Restore"
        onCancel={() => setRestoring(undefined)}
        onConfirm={async () => {
          try {
            await routes.restoreMeshPolicyVersion(restoring!.id);
            setError(undefined);
            versions.reload();
            onRestored();
          } catch (e) {
            setError(problemText(e));
          }
          setRestoring(undefined);
        }}
      >
        <p>
          The policy {restoring?.savedByName ? `${restoring.savedByName} published` : 'published'} {restoring ? timeAgo(restoring.savedAt) : ''} becomes the policy in force. If any of its tests fail against the machines on the mesh now, it is refused and nothing changes.
        </p>
      </ConfirmDialog>
    </div>
  );
}

export function MeshDomains({ tone: _tone, nodes }: { tone: ArtTone; nodes: readonly MeshNode[] }) {
  const suffix = nodes.find((n) => n.dnsName)?.dnsName?.replace(/^[^.]+\./, '');
  return (
    <ListGroup title="Domains" description="Where the mesh's names live."><ListBlock>
      <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-muted">Name suffix</dt>
        <dd className="mono">.{suffix ?? 'mesh.internal'}</dd>
        <dt className="text-muted">Resolves</dt>
        <dd>Only inside the mesh, for machines the policy lets reach each other. Nothing is published to public DNS.</dd>
        <dt className="text-muted">Custom domains</dt>
        <dd className="text-muted">Not offered, by design: a name that cannot leave the mesh cannot clash with one outside it.</dd>
      </dl>
    </ListBlock></ListGroup>
  );
}

export function MeshJoinKeys({ tone: _tone }: { tone: ArtTone }) {
  const keys = useLoad(() => routes.joinKeys(), []);
  const [error, setError] = useState<string>();
  const [now] = useState(() => Date.now());
  const rows = keys.data ?? [];
  return (
    <ListGroup title="Keys" count={rows.filter((k) => !k.revokedAt && Date.parse(k.expiresAt) > now).length} description="Join keys let a machine join without waiting for approval. New keys are made under Nodes." actions={
        <Link to="/nodes" className="text-sm text-accent hover:underline">
          Make a key
        </Link>
      }><ListBlock flush>
      <ErrorNote error={keys.error ?? error} />
      <ul className="divide-y divide-separator text-sm" role="list">
        {rows.map((k) => {
          const live = !k.revokedAt && Date.parse(k.expiresAt) > now;
          return (
            <li key={k.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0">
                <span className="block truncate font-medium">{k.name}</span>
                <span className="block truncate text-xs text-muted">
                  {k.reusable ? 'Reusable' : 'One use'}
                  {k.ephemeral ? ' · Ephemeral' : ''}
                  {k.tags.length ? ` · ${k.tags.join(', ')}` : ''} · made by {k.createdBy} · {k.uses} {k.uses === 1 ? 'use' : 'uses'}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {k.revokedAt ? <StatusMark state="disabled" label="Revoked" /> : live ? <StatusMark state="online" label={`Until ${dateShort(k.expiresAt)}`} /> : <StatusMark state="offline" label="Expired" />}
                {live && (
                  <Button
                    size="sm"
                    variant="danger-soft"
                    onPress={() =>
                      void routes.revokeJoinKey(k.id).then(
                        () => keys.reload(),
                        (e: unknown) => setError(problemText(e)),
                      )
                    }
                  >
                    Revoke
                  </Button>
                )}
              </span>
            </li>
          );
        })}
        {rows.length === 0 && !keys.loading && <li className="px-4 py-3 text-muted">No join keys. A machine can still join by signing in, or by waiting for approval.</li>}
      </ul>
    </ListBlock></ListGroup>
  );
}
